import React, { useCallback, MutableRefObject } from 'react'

import { AddressSearchTabs } from '~/components/component-library/AddressSearchInput/components/AddressSearchTabs'
import { SearchResult } from '~/components/component-library/AddressSearchInput/components/SearchResult'
import {
  AddressSearchTabId,
  AddressSearchTabItem,
  AddressSearchTabSection,
} from '~/components/component-library/AddressSearchInput/types'
import { EmptyState } from '~/components/component-library/EmptyState'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { PopoverContent } from '~/components/ui/popover'
import { IAddress, AddressSearchResult } from '~/types'

const EMPTY_STATE_COPY: Record<AddressSearchTabId, string> = {
  all: 'No results',
  recents: 'No recent recipients',
  accounts: 'No matching accounts',
  banks: 'No matching bank accounts',
  contacts: 'No matching contacts',
}

type AddressSearchDropdownProps = {
  isLoading: boolean
  tabs: AddressSearchTabItem[]
  activeTab: AddressSearchTabId
  onTabChange: (tab: AddressSearchTabId) => void
  sections: AddressSearchTabSection[]
  highlightText: string
  currentFocus: number
  setFocus: (index: number) => void
  selectResult: (result: AddressSearchResult) => void
  removeFromSearchHistory: (address: IAddress) => void
  resultsRef: MutableRefObject<(HTMLButtonElement | null)[]>
}

export const AddressSearchDropdown = ({
  isLoading,
  tabs,
  activeTab,
  onTabChange,
  sections,
  highlightText,
  currentFocus,
  setFocus,
  selectResult,
  removeFromSearchHistory,
  resultsRef,
}: AddressSearchDropdownProps) => {
  const registerResultRef = useCallback(
    (index: number) => (element: HTMLButtonElement | null) => {
      resultsRef.current[index] = element
    },
    [resultsRef],
  )

  let nextIndex = 0

  const renderResultItem = (
    result: AddressSearchResult,
    { allowRemove }: { allowRemove?: boolean } = {},
  ) => {
    const index = nextIndex++

    return (
      <SearchResult
        key={`${result.address}-${index}`}
        result={result}
        highlightText={highlightText}
        isFocused={currentFocus === index}
        selectResult={selectResult}
        onMouseEnter={() => setFocus(index)}
        removeResult={
          allowRemove
            ? () => removeFromSearchHistory(result.address)
            : undefined
        }
        registerRef={registerResultRef(index)}
      />
    )
  }

  const renderSection = (
    { title, rows, allowRemove }: AddressSearchTabSection,
    sectionIndex: number,
  ) => (
    <React.Fragment key={title ?? sectionIndex}>
      {title ? (
        <div className="flex justify-between px-2.5 py-1.5 text-xs text-secondary">
          {title}
        </div>
      ) : null}
      {rows.map((row) => renderResultItem(row, { allowRemove }))}
    </React.Fragment>
  )

  const renderContent = () => {
    // Resolution lookups only run against a typed query, so the spinner only
    // ever applies to the unified results view.
    if (isLoading && activeTab === 'all') {
      return (
        <div className="flex items-center space-x-2 p-3 text-secondary">
          <LoadingSpinner size="xl" />
          <div>Searching...</div>
        </div>
      )
    }

    if (!sections.length) {
      return (
        <div className="flex h-20 w-full items-center p-1">
          <EmptyState className="w-full bg-transparent text-center text-sm dark:bg-transparent">
            {EMPTY_STATE_COPY[activeTab]}
          </EmptyState>
        </div>
      )
    }

    return sections.map(renderSection)
  }

  return (
    <PopoverContent
      align="start"
      sideOffset={4}
      // Keep focus in the search input so the user can keep typing
      onOpenAutoFocus={(e) => e.preventDefault()}
      onCloseAutoFocus={(e) => e.preventDefault()}
      // The popover only stays open while the input has focus — swallow mouse
      // presses anywhere inside (tab gaps, padding, scrollbar) so they don't
      // blur the input and close the dropdown.
      onMouseDown={(e) => e.preventDefault()}
      style={{
        width: 'var(--radix-popover-trigger-width)',
        maxHeight:
          'min(24rem, calc(var(--radix-popover-content-available-height) - 16px))',
      }}
      // Trigger extends straight down — cancel anchored's scale-in
      className="flex flex-col gap-0.5 overflow-hidden p-1 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100"
    >
      {tabs.length > 1 && (
        <AddressSearchTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      )}
      <div
        data-scroll-container
        className="min-h-0 flex-1 overflow-y-auto"
        onWheel={(e) => {
          e.stopPropagation()
        }}
      >
        {renderContent()}
      </div>
    </PopoverContent>
  )
}
